/**
 * NewDashboardScreen.js
 *
 * Premium dashboard with:
 *  - DateRangePicker filter integration
 *  - Overview, SDR Activity, Campaigns tabs
 *  - Hero KPI cards, KPI strips, Pipeline Funnel, Charts, Tables
 *
 * COMPONENT REUSE:
 *  - CustomHeader (from components/Header)
 *  - FilterTab (from components/FilterTab)
 *  - StatusChip (from components/StatusChip)
 *  - DateRangePicker (from components/DateRangePicker)
 *
 * COLOR POLICY:
 *  All colors consumed via CSS custom properties (--nd-*) set on
 *  the root container from JS color tokens. Zero hardcoded hex.
 */
import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTour } from "../../context/TourContext";
import {
  getNewDashboardData,
  getCampaignInsights,
  getEngagementDetails,
  getCampaignDashboardData,
  getMeetingLogs,
} from "../../redux/actions/dashboardActions";
import { isDemoTenant } from "../../utils/helpers/tenantConfig";
import {
  isRestrictedTenant,
  isRepliesDashboardTenant,
  isDashboardUnrestrictedTenant,
  isEmailOnlyOutreachChartTenant,
} from "../../utils/helpers/tenantRestrictions";
import CustomHeader from "../../components/Header/CustomHeader";
import FilterTab from "../../components/FilterTab/FilterTab";
import DashboardLoader from "../../components/Loaders/DashboardLoader";
import StatusChip from "../../components/StatusChip/StatusChip";
import IconComponent from "../../components/Icon/IconComponent";
import { ICON_LABELS } from "../../utils/constants/UI";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker/DateRangePicker";
import DashboardDateRangePicker from "./components/DashboardDateRangePicker";
import RepliesListDialog from "./components/RepliesListDialog";
import { STRINGS } from "./strings";
import {
  BRAND,
  SUCCESS,
  HELP,
  TEAL,
  PURPLE,
  INDIGO,
} from "../../utils/constants/colors";
import DashboardChart from "./DashboardChart";
import {
  CSS_VARS,
  TAB_CONFIG,
  getOutreachByChannelConfig,
  getMeetingsByChannelConfig,
  getMeetingsBySeniorityConfig,
  getPipelineSnapshotConfig,
  getDailyActivityTrendConfig,
  getChannelMixConfig,
  getReplySentimentConfig,
  getReplyRateTrendConfig,
} from "./newDashboardData";
import styles from "./NewDashboard.module.css";
import CustomTable from "../../components/Table/CustomTable";

const formatDateForQuery = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Day-boundary helpers — always compute in local time so the calendar day
// in the UI matches the from/to we send.
const startOfDay = (input) => {
  const d = input instanceof Date ? new Date(input) : new Date(input);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const endOfDay = (input) => {
  const d = input instanceof Date ? new Date(input) : new Date(input);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};

const addDays = (ts, n) => {
  const d = new Date(ts);
  d.setDate(d.getDate() + n);
  return d.getTime();
};

const fmt = (ts) => (ts == null ? "null" : new Date(ts).toString());

// Local YYYY-MM-DD (browser timezone). MUST NOT use toISOString — that
// returns UTC and shifts the date for any non-UTC user, which is exactly
// the bug overview-stats is fixing. 'en-CA' locale formats as YYYY-MM-DD
// in the browser's local tz, so IST/US/etc. all resolve to the right day.
const toLocalDate = (ts) =>
  ts == null ? null : new Date(ts).toLocaleDateString("en-CA");

const normalizeDashboardDateRange = (selection) => {
  if (!selection) {
    console.log("[DateRange] selection is null/undefined → no filter");
    return null;
  }

  const label = selection?.label;
  // Picker may emit endpoints flat (selection.from/.to) or nested
  // (selection.dates.from/.to) — accept either.
  const fromDate = selection?.from ?? selection?.dates?.from;
  const toDate = selection?.to ?? selection?.dates?.to;
  const todayStart = startOfDay(Date.now());
  const todayEnd = endOfDay(Date.now());

  let result;
  switch (label) {
    case "today":
      // Today 00:00:00 → Today 23:59:59
      result = { from: todayStart, to: todayEnd };
      break;
    case "yesterday":
      // Yesterday 00:00:00 → Yesterday 23:59:59
      result = {
        from: startOfDay(addDays(todayStart, -1)),
        to: endOfDay(addDays(todayStart, -1)),
      };
      break;
    case "last_7_days":
      // Includes today: 6 days ago 00:00:00 → today 23:59:59 (7 calendar days)
      result = {
        from: startOfDay(addDays(todayStart, -6)),
        to: todayEnd,
      };
      break;
    case "last_30_days":
      // Includes today: 29 days ago 00:00:00 → today 23:59:59 (30 calendar days)
      result = {
        from: startOfDay(addDays(todayStart, -29)),
        to: todayEnd,
      };
      break;
    case "all_time":
      console.log("[DateRange] label=all_time → no filter sent");
      return null;
    case "custom_range":
      if (!fromDate || !toDate || toDate === -1) {
        console.log("[DateRange] custom_range missing endpoints", {
          fromDate,
          toDate,
        });
        return null;
      }
      // Snap to full-day boundaries so the picked range is inclusive on both ends.
      result = { from: startOfDay(fromDate), to: endOfDay(toDate) };
      break;
    default:
      if (!fromDate || !toDate || toDate === -1) {
        console.log("[DateRange] default branch, missing endpoints", {
          label,
          fromDate,
          toDate,
        });
        return null;
      }
      result = { from: startOfDay(fromDate), to: endOfDay(toDate) };
      break;
  }

  console.log("[DateRange] label=", label, {
    from_epoch: result.from,
    to_epoch: result.to,
    from_local: fmt(result.from),
    to_local: fmt(result.to),
    from_iso: new Date(result.from).toISOString(),
    to_iso: new Date(result.to).toISOString(),
  });
  return result;
};

const getDefaultDashboardDateRange = () => ({
  label: "all_time",
});

// ═══════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════

/** KPI strip row - items with an onClick open a drill-down */
const KpiStrip = ({ items }) => (
  <div className={styles.kpis}>
    {items.map((k, i) => (
      <div
        className={
          k.onClick ? `${styles.kpi} ${styles.kpiClickable}` : styles.kpi
        }
        key={i}
        style={{
          animationDelay: `${0.1 + i * 0.06}s`,
        }}
        onClick={k.onClick}
        role={k.onClick ? "button" : undefined}
        tabIndex={k.onClick ? 0 : undefined}
        onKeyDown={
          k.onClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  k.onClick();
                }
              }
            : undefined
        }
      >
        <div className={styles.kpiLabel}>
          <span className={styles.kpiDot} style={{ background: k.dotColor }} />
          {k.label}
        </div>
        <div className={styles.kpiVal}>{k.value}</div>
      </div>
    ))}
  </div>
);

/** Funnel visualization with animated bars */
const Funnel = ({ data }) => (
  <div className={styles.funnel}>
    {data.map((f, i) => (
      <div className={styles.fRow} key={i}>
        <div className={styles.fName}>{f.name}</div>
        <div className={styles.fTrack}>
          <div
            className={styles.fFill}
            style={{
              width: f.width,
              background: f.color,
              minWidth: f.minW || "0px",
              animationDelay: `${i * 0.08}s`,
            }}
          />
        </div>
        <div className={styles.fNum}>{f.value}</div>
        <div className={styles.fPct}>{f.pct}</div>
      </div>
    ))}
  </div>
);

/** Pill renderer - maps signal/label to correct CSS class */
const Pill = ({ label }) => {
  const classMap = {
    Hot: styles.pillHot,
    Warm: styles.pillWarm,
    Active: styles.pillActive,
    Done: styles.pillDone,
    Email: styles.pillEmail,
    Call: styles.pillCall,
    "Multi-touch": styles.pillMulti,
    "C-Suite": styles.pillCSuite,
    VP: styles.pillVP,
    Director: styles.pillDirector,
  };
  return <span className={classMap[label] || styles.pill}>{label}</span>;
};

/** Card component */
const Card = ({ children, className, ...rest }) => (
  <div className={`${styles.card} ${className || ""}`} {...rest}>{children}</div>
);

/** Card Header */
const CardHead = ({ title, subtitle, badge, badgeClass, ...rest }) => (
  <div className={styles.cardHeader} {...rest}>
    <div className={styles.cardHeaderRow}>
      <div>
        <div className={styles.cardTitle}>{title}</div>
        {subtitle && <div className={styles.cardSubtitle}>{subtitle}</div>}
      </div>
    </div>
    {badge && <span className={badgeClass || styles.badgeGreen}>{badge}</span>}
  </div>
);

// ═══════════════════════════════════════════════════════════
// HERO CARD CLASS MAP
// ═══════════════════════════════════════════════════════════
const HERO_CLASS_MAP = {
  primary: styles.heroCardPrimary,
  green: styles.heroCardGreen,
  blue: styles.heroCardBlue,
  indigo: styles.heroCardIndigo,
};

// ═══════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════

/* OLD STATIC IMPLEMENTATION
const OverviewTabOld = () => {
  return (
    <>
      <div className={styles.heroRow}>
        {HERO_KPIS.map((h, i) => (
          <div
            className={HERO_CLASS_MAP[h.color]}
            key={i}
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <div className={styles.heroTopRow}>
              <div className={styles.heroLabel}>{h.label}</div>
              <div className={styles.heroIconBox}>
                <IconComponent iconLabel={ICON_LABELS[h.icon]} />
              </div>
            </div>
            <div className={styles.heroVal}>{h.value}</div>
            <span className={styles.heroTrendUp}>{h.trend}</span>
            <div className={styles.heroNote}>{h.note}</div>
          </div>
        ))}
      </div>

      <KpiStrip items={OVERVIEW_KPIS} />

      <div className={styles.gw}>
        <Card>
          <CardHead
            title="Meetings Booked - Weekly Trend"
            subtitle="Tracking above target"
            badge="▲ On Track"
          />
          <DashboardChart config={getMeetingsTrendConfig()} height={200} />
        </Card>
        <Card>
          <CardHead title="Pipeline Funnel" subtitle="Outreach to meeting" />
          <Funnel data={PIPELINE_FUNNEL} />
        </editorCards>
      </div>
      ... (rest of old static structure) ...
    </>
  );
};
*/

// Dummy data shown only while the dashboard tour is active.
// Mirrors the real overview + insights payload shape so OverviewTab renders identically.
const DASHBOARD_TOUR_DUMMY_OVERVIEW = {
  kpi_cards: {
    meetings_booked:  { value: 1, completed: 1, show_rate: 100 },
    qualified_leads:  { value: 6 },
    total_outreach:   { value: 100 },
    total_replies:    { value: 25 },
    active_campaigns: { value: 6 },
    emails_sent:      { value: 240 },
    calls_made:       { value: 28 },
    linkedin_activity: { sent_msg_linkedIn: 18, connection_linkedIn: 24, respond_linkedIn: 9 },
    total_campaigns:  { value: 14 },
  },
  pipeline_funnel: {
    contacted:      { value: 100 },
    opened:         { value: 85, rate: 85 },
    replied:        { value: 25, rate: 29.41 },
    interested:     { value: 6,  rate: 24 },
    meeting_booked: { value: 1,  rate: 16.66 },
  },
};

const DASHBOARD_TOUR_DUMMY_INSIGHTS = {
  top_campaigns: [
    { name: "Q2 SaaS Outbound",          replies: 11, meetings: 1 },
    { name: "Mid-Market Cold Outreach",   replies: 7,  meetings: 0 },
    { name: "Enterprise Decision Makers", replies: 4,  meetings: 0 },
    { name: "Product Demo Follow-up",     replies: 2,  meetings: 0 },
    { name: "Inbound Lead Nurture",       replies: 1,  meetings: 0 },
  ],
};

// Campaigns tab — KPI cards + performance table shown during the dashboard tour.
// Totals are internally consistent:
//   emails_sent (240) = sum of sent across all 8 rows (240)
//   opened total (204) / sent (240) = 85% → matches open_rate KPI
//   replied total (25) = total_replies from overview
//   meetings total (1) = meetings_booked from overview
//   rows 1–5 reuse the same campaign names as top_campaigns in the overview tab
const DASHBOARD_TOUR_DUMMY_CAMPAIGN_DASHBOARD = {
  kpi_cards: {
    emails_sent: { value: 240 },
    open_rate:   { value: 85 },
    reply_rate:  { value: 25 },
    meetings:    { value: 1 },
  },
  campaign_performance: [
    { campaign_id: 1, name: "Q2 SaaS Outbound",          status: "active",    sent: 45, opened: 40, open_rate: 89, replied: 11, reply_rate: 24, meetings: 1 },
    { campaign_id: 2, name: "Mid-Market Cold Outreach",   status: "active",    sent: 50, opened: 44, open_rate: 88, replied: 7,  reply_rate: 14, meetings: 0 },
    { campaign_id: 3, name: "Enterprise Decision Makers", status: "completed", sent: 46, opened: 38, open_rate: 83, replied: 4,  reply_rate: 9,  meetings: 0 },
    { campaign_id: 4, name: "Product Demo Follow-up",     status: "completed", sent: 38, opened: 32, open_rate: 84, replied: 2,  reply_rate: 5,  meetings: 0 },
    { campaign_id: 5, name: "Inbound Lead Nurture",       status: "completed", sent: 26, opened: 22, open_rate: 85, replied: 1,  reply_rate: 4,  meetings: 0 },
    { campaign_id: 6, name: "SDR Intro Sequence",         status: "completed", sent: 16, opened: 13, open_rate: 81, replied: 0,  reply_rate: 0,  meetings: 0 },
    { campaign_id: 7, name: "Warm Re-engagement",         status: "paused",    sent: 12, opened: 10, open_rate: 83, replied: 0,  reply_rate: 0,  meetings: 0 },
    { campaign_id: 8, name: "LinkedIn Connection Series", status: "active",    sent: 7,  opened: 5,  open_rate: 71, replied: 0,  reply_rate: 0,  meetings: 0 },
  ],
  meta: {
    campaign_pagination: { total_pages: 1, total: 8 },
  },
};

const DASHBOARD_TOUR_DUMMY_ENGAGEMENT = {
  hot_leads: [
    { first_name: "Sarah", last_name: "Mitchell", company_name: "Nexlify", job_title: "Head of Growth", engagement_score: 94, last_activity_date: "2026-07-05", signal: "hot" },
    { first_name: "Daniel", last_name: "Okafor",  company_name: "Stackwise",  job_title: "VP of Sales",      engagement_score: 88, last_activity_date: "2026-07-04", signal: "hot" },
    { first_name: "Priya", last_name: "Sharma",   company_name: "Orbient",    job_title: "Marketing Manager", engagement_score: 61, last_activity_date: "2026-07-03", signal: "warm" },
  ],
};

const OverviewTab = ({
  overview,
  insights,
  engagement,
  meetingLogsData,
  meetingPage,
  onMeetingPageChange,
  onTotalRepliesClick,
}) => {
  const kpis = overview?.kpi_cards || {};
  // Dashboard-unrestricted tenants (e.g. workdigital) are restricted app-wide
  // but render the dashboard exactly like get-eturns: full KPI strip and no
  // extra "Emails Sent" hero card. Only the dashboard treats them as
  // unrestricted; every other screen still uses isRestrictedTenant() directly.
  const restrictedTenant =
    isRestrictedTenant() && !isDashboardUnrestrictedTenant();
  // Replies-dashboard tenants swap the "Total Outreach" hero card for a
  // clickable "Total Replies" card (opens the same replies drill-down).
  const repliesDashboard = isRepliesDashboardTenant();

  const heroKpis = [
    {
      label: "Meetings Booked",
      value: kpis.meetings_booked?.value || "0",
      color: "primary",
      icon: "EVENT_NOTE",
      note: `${kpis.meetings_booked?.completed || 0} completed · ${kpis.meetings_booked?.show_rate || 0}% show rate`,
    },
    {
      label: "Qualified Leads",
      value: kpis.qualified_leads?.value || "0",
      color: "green",
      icon: "HANDSHAKE",
      note: "ICP-fit contacts showing interest",
    },
    repliesDashboard
      ? {
          label: "Total Replies",
          value: kpis.total_replies?.value?.toLocaleString() || "0",
          color: "blue",
          icon: "REPLY",
          note: "Replies across campaigns",
          onClick: onTotalRepliesClick,
        }
      : {
          label: "Total Outreach",
          value: kpis.total_outreach?.value?.toLocaleString() || "0",
          color: "blue",
          icon: "CAMPAIGN",
          note: "Emails & calls combined",
        },
    {
      label: "Active Campaigns",
      value: kpis.active_campaigns?.value || "0",
      color: "indigo",
      icon: "GRID_VIEW",
      note: "Currently running campaigns",
    },
    ...(restrictedTenant
      ? [
          {
            label: "Emails Sent",
            value: kpis.emails_sent?.value?.toLocaleString() || "0",
            color: "primary",
            icon: "EMAIL",
            note: "Emails sent across campaigns",
          },
        ]
      : []),
  ];

  const stripKpis = [
    {
      label: "Emails Sent",
      value: kpis.emails_sent?.value?.toLocaleString() || "0",
      dotColor: "#4F46E5",
    },
    {
      label: "Calls Made",
      value: kpis.calls_made?.value?.toLocaleString() || "0",
      dotColor: "#F59E0B",
    },
    // {
    //   label: "Reply Rate",
    //   value: `${kpis.reply_rate?.value || 0}%`,
    //   dotColor: "#14B8A6",
    // },
    {
      label: "Total Opens",
      value: `${overview?.pipeline_funnel?.opened?.value?.toLocaleString() || 0}`,
      dotColor: "#14B8A6",
    },
    {
      label: "Total Replies",
      value: kpis.total_replies?.value?.toLocaleString() || "0",
      dotColor: "#7C3AED",
      onClick: onTotalRepliesClick,
    },
    {
      label: "LinkedIn Messages",
      value: kpis.linkedin_activity?.sent_msg_linkedIn?.toLocaleString() || "0",
      dotColor: "#0077B5",
    },
    {
      label: "LinkedIn Requests",
      value:
        kpis.linkedin_activity?.connection_linkedIn?.toLocaleString() || "0",
      dotColor: "#0077B5",
    },
    {
      label: "LinkedIn Responses",
      value: kpis.linkedin_activity?.respond_linkedIn?.toLocaleString() || "0",
      dotColor: "#0077B5",
    },
    {
      label: "Total Campaigns",
      value: kpis.total_campaigns?.value || "0",
      dotColor: "#A1A0A3",
    },
  ];

  const pf = overview?.pipeline_funnel;
  const funnelData = pf
    ? [
        {
          name: "Contacted",
          value: pf.contacted?.value?.toLocaleString() ?? "0",
          pct: "",
          width: pf.contacted?.value > 0 ? "100%" : "0%",
          color: INDIGO.W_500,
        },
        {
          name: "Opened",
          value: pf.opened?.value?.toLocaleString() ?? "0",
          pct: `${pf.opened?.rate ?? 0}%`,
          width: `${pf.opened?.rate ?? 0}%`,
          color: PURPLE.W_500,
        },
        {
          name: "Replied",
          value: pf.replied?.value?.toLocaleString() ?? "0",
          pct: `${pf.replied?.rate ?? 0}%`,
          width: `${pf.replied?.rate ?? 0}%`,
          color: TEAL.W_500,
        },
        {
          name: "Interested",
          value: pf.interested?.value?.toLocaleString() ?? "0",
          pct: `${pf.interested?.rate ?? 0}%`,
          width: `${pf.interested?.rate ?? 0}%`,
          color: HELP.W_500,
        },
        {
          name: "Meeting",
          value: pf.meeting_booked?.value?.toLocaleString() ?? "0",
          pct: `${pf.meeting_booked?.rate ?? 0}%`,
          width: `${pf.meeting_booked?.rate ?? 0}%`,
          color: BRAND.PRIMARY,
        },
      ]
    : [];

  const snapshotData = overview?.pipeline_snapshot
    ? {
        labels: Object.keys(overview.pipeline_snapshot).map(
          (k) => k.charAt(0).toUpperCase() + k.slice(1),
        ),
        data: Object.values(overview.pipeline_snapshot).map((v) => v.value),
      }
    : null;

  return (
    <>
      {/* Hero Cards + KPI Strip */}
      <div data-tour="dashboard-kpi-section">
      {/* Hero Cards */}
      <div
        className={styles.heroRow}
        style={
          restrictedTenant
            ? { gridTemplateColumns: `repeat(${heroKpis.length}, 1fr)` }
            : undefined
        }
      >
        {heroKpis.map((h, i) => (
          <div
            className={
              h.onClick
                ? `${HERO_CLASS_MAP[h.color]} ${styles.heroClickable}`
                : HERO_CLASS_MAP[h.color]
            }
            key={i}
            style={{ animationDelay: `${i * 0.1}s` }}
            onClick={h.onClick}
            role={h.onClick ? "button" : undefined}
            tabIndex={h.onClick ? 0 : undefined}
            onKeyDown={
              h.onClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      h.onClick();
                    }
                  }
                : undefined
            }
          >
            <div className={styles.heroTopRow}>
              <div className={styles.heroLabel}>{h.label}</div>
              <div className={styles.heroIconBox}>
                <IconComponent iconLabel={ICON_LABELS[h.icon]} />
              </div>
            </div>
            <div className={styles.heroVal}>{h.value}</div>
          </div>
        ))}
      </div>

      {/* KPI Strip - hidden for restricted tenant (Emails Sent moved into hero row) */}
      {!restrictedTenant && <KpiStrip items={stripKpis} />}
      </div>

      {/* Outreach trend - full width, it carries the top chart slot now that
          the "Meetings Booked - Weekly Trend" card has been removed. */}
      <div className={styles.g1}>
        <Card>
          <CardHead
            title="Outreach by Channel"
            subtitle="Weekly volume breakdown"
          />
          <DashboardChart
            config={getOutreachByChannelConfig(insights?.outreach_by_channel, {
              emailOnly: isEmailOnlyOutreachChartTenant(),
            })}
            height={240}
          />
        </Card>
      </div>

      {/* Funnel & Campaigns */}
      <div className={styles.g2}>
        <Card data-tour="dashboard-pipeline-funnel">
          <CardHead title="Pipeline Funnel" subtitle="Outreach to meeting" />
          <Funnel data={funnelData} />
        </Card>
        <Card data-tour="dashboard-top-campaigns">
          <CardHead title="Top Campaigns" subtitle="By meetings booked" />
          <table className={styles.tbl}>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Replies</th>
                <th>Meetings</th>
              </tr>
            </thead>
            <tbody>
              {(insights?.top_campaigns || []).slice(0, 5).map((c, i) => (
                <tr key={i}>
                  <td className={styles.tdBold}>{c.name || c.campaign}</td>
                  <td>{c.replies}</td>
                  <td className={styles.tdAccent}>{c.meetings}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Seniority - Commented out as requested */}
      {/* 
      <div className={styles.g2}>
        <Card>
          <CardHead title="Meetings by Channel" subtitle="Source attribution" />
          <DashboardChart
            config={getMeetingsByChannelConfig(insights?.meetings_by_channel)}
            height={220}
          />
        </Card>
        <Card>
          <CardHead
            title="Meetings by Seniority"
            subtitle="C-Suite, VP, Director"
          />
          <DashboardChart
            config={getMeetingsBySeniorityConfig(
              insights?.meetings_by_seniority,
            )}
            height={220}
          />
        </Card>
      </div> 
      */}

      {/* Top Contacts */}
      <div className={styles.g1} data-tour="dashboard-top-contacts-table">
        <Card>
          <CardHead
            title="Top Engaged Contacts"
            subtitle="Contacts with recent opens and replies"
            data-tour="dashboard-top-contacts-header"
          />
          <table className={styles.tbl}>
            <thead>
              <tr>
                <th>Contact</th>
                <th>Company</th>
                <th>Title</th>
                <th>Engagement Score</th>
                <th>Last Activity</th>
                <th>Signal</th>
              </tr>
            </thead>
            <tbody>
              {(engagement?.hot_leads || []).map((c, i) => (
                <tr key={i}>
                  <td className={styles.tdBold}>
                    {c.first_name ? `${c.first_name} ${c.last_name}` : c.name}
                  </td>
                  <td>{c.company_name || c.company}</td>
                  <td>{c.job_title || c.title}</td>
                  <td style={{ fontSize: "11.5px" }}>
                    {c.engagement_score || c.activity}
                  </td>
                  <td className={styles.textMuted}>
                    {c.last_activity_date
                      ? new Date(c.last_activity_date).toLocaleDateString()
                      : c.last}
                  </td>
                  <td>
                    <Pill
                      label={
                        c.signal === "hot"
                          ? "Hot"
                          : c.signal === "warm"
                            ? "Warm"
                            : c.signal
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* ICP & Pipeline - Commented out as requested */}
      {/* 
      <div className={styles.g2}>
        <Card>
          <CardHead
            title="ICP Match Overview"
            subtitle="How well prospects align with your Ideal Customer Profile"
            badge={`${engagement?.icp_match?.seniority_fit || 91.5}% Match`}
          />
          <div className={styles.icpContent}>
            <div className={styles.icpStatsRow}>
              {[
                {
                  label: "Match Score",
                  value: `${engagement?.icp_match?.seniority_fit || 91.5}%`,
                  color: "#01A63E",
                },
                {
                  label: "Total Contacts",
                  value:
                    engagement?.icp_match?.total_contacts?.toLocaleString() ||
                    "3,480",
                  color: "#4F46E5",
                },
                {
                  label: "Tech Fit",
                  value: `${engagement?.icp_match?.tech_stack_fit || 83}%`,
                  color: "#9162FA",
                },
              ].map((s, i) => (
                <div className={styles.icpStatBox} key={i}>
                  <div className={styles.icpStatVal} style={{ color: s.color }}>
                    {s.value}
                  </div>
                  <div className={styles.icpStatLabel}>{s.label}</div>
                </div>
              ))}
            </div>
            <div>
              {(engagement?.icp_match?.industry_breakdown || ICP_BARS)
                .slice(0, 5)
                .map((b, i) => (
                  <div className={styles.icpRow} key={i}>
                    <div className={styles.icpRowLabel}>
                      {b.industry || b.label}
                    </div>
                    <div className={styles.icpRowBar}>
                      <div
                        className={styles.icpRowFill}
                        style={{
                          width: `${b.count ? ((b.count / engagement.icp_match.total_contacts) * 100).toFixed(1) : b.pct}%`,
                          background: i % 2 === 0 ? "#4F46E5" : "#9162FA",
                        }}
                      />
                    </div>
                    <div
                      className={styles.icpRowPct}
                      style={{ color: i % 2 === 0 ? "#4F46E5" : "#9162FA" }}
                    >
                      {b.count
                        ? (
                            (b.count / engagement.icp_match.total_contacts) *
                            100
                          ).toFixed(1)
                        : b.pct}
                      %
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Card>
        <Card>
          <CardHead
            title="Pipeline Snapshot"
            subtitle="Lead to opportunity journey"
          />
          <DashboardChart
            config={getPipelineSnapshotConfig(snapshotData)}
            height={220}
          />
        </Card>
      </div> 
      */}

      {/* Meeting Log - hidden for demo tenant */}
      {!isDemoTenant() && <div className={styles.g1}>
        <Card>
          <CardHead
            title="Meeting Log"
            subtitle="All qualified meetings this month"
          />
          {!meetingLogsData?.loading &&
          !meetingLogsData?.payload?.records?.length ? (
            <div className={styles.emptyMeetings}>
              <div className={styles.emptyMeetingsIcon}>
                <IconComponent
                  iconLabel={ICON_LABELS.EVENT_NOTE}
                  fontSize="28px"
                />
              </div>
              <div className={styles.emptyMeetingsTitle}>No meetings yet</div>
              <div className={styles.emptyMeetingsSubtitle}>
                Meetings booked through your campaigns will appear here
              </div>
            </div>
          ) : (
            <div className={styles.overflowAuto}>
              <CustomTable
                tableData={{
                  id: "meeting_log_table",
                  heads: [
                    {
                      id: "date",
                      label: "Date",
                      ui_id: "bold_text",
                      width: "10vw",
                    },
                    {
                      id: "time",
                      label: "Time",
                      ui_id: "text",
                      width: "6vw",
                    },
                    {
                      id: "prospect_name",
                      label: "Contact",
                      ui_id: "text",
                      width: "14vw",
                    },
                    {
                      id: "prospect_email",
                      label: "Email",
                      ui_id: "text",
                      width: "12vw",
                    },
                    {
                      id: "duration",
                      label: "Duration",
                      ui_id: "text",
                      width: "8vw",
                    },
                    {
                      id: "status",
                      label: "Status",
                      ui_id: "text",
                      width: "8vw",
                    },
                    {
                      id: "description",
                      label: "Description",
                      ui_id: "text",
                      width: "32vw",
                    },
                  ],
                  body: meetingLogsData?.payload?.records || [],
                  isLoading: meetingLogsData?.loading,
                  total_records: meetingLogsData?.payload?.total || 0,
                  is_selection_not_allowed: true,
                }}
              />
            </div>
          )}
        </Card>
      </div>}
    </>
  );
};

// ═══════════════════════════════════════════════════════════
// SDR ACTIVITY TAB
// ═══════════════════════════════════════════════════════════

const ActivityTab = ({ insights }) => {
  return (
    <>
      {/* KPI Strip */}
      <KpiStrip items={[]} />

      {/* Activity Trends */}
      <div className={styles.g2}>
        <Card>
          <CardHead
            title="Daily Activity Trend"
            subtitle="Last 2 weeks by channel"
          />
          <DashboardChart config={getDailyActivityTrendConfig()} height={220} />
        </Card>
        <Card>
          <CardHead title="Channel Mix" subtitle="Effort distribution" />
          <DashboardChart
            config={getChannelMixConfig(insights?.meetings_by_channel)}
            height={220}
          />
        </Card>
      </div>

      {/* SDR Leaderboard */}
      <div className={styles.g1}>
        <Card>
          <CardHead
            title="SDR Leaderboard"
            subtitle="Individual performance this month"
          />
          <div>
            {[].map((s, i) => {
              const rankClass =
                s.rank === 1
                  ? styles.ldrRank1
                  : s.rank === 2
                    ? styles.ldrRank2
                    : styles.ldrRank3;
              return (
                <div className={styles.ldrRow} key={i}>
                  <div className={rankClass}>{s.rank}</div>
                  <div className={styles.ldrInfo}>
                    <div className={styles.ldrName}>{s.name}</div>
                    <div className={styles.ldrStats}>{s.stats}</div>
                  </div>
                  <div className={styles.ldrBarW}>
                    <div className={styles.ldrBarBg}>
                      <div
                        className={styles.ldrBarFill}
                        style={{ width: s.barWidth }}
                      />
                    </div>
                  </div>
                  <div className={styles.ldrVal}>{s.meetings}</div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════
// CAMPAIGNS TAB
// ═══════════════════════════════════════════════════════════

const CampaignsTab = ({ campaignDashboard, onPageChange, page }) => {
  const data = campaignDashboard?.payload;
  const kpis = data?.kpi_cards || {};
  const campaignPerformance = data?.campaign_performance || [];
  const pagination = data?.meta?.campaign_pagination;
  const totalPages = pagination?.total_pages || 1;

  const stripKpis = [
    {
      label: "Emails Sent",
      value: kpis.emails_sent?.value?.toLocaleString() || "0",
      dotColor: "#4F46E5",
    },
    {
      label: "Open Rate",
      value: `${kpis.open_rate?.value || 0}%`,
      dotColor: "#14B8A6",
    },
    {
      label: "Reply Rate",
      value: `${kpis.reply_rate?.value || 0}%`,
      dotColor: "#9162FA",
    },
    {
      label: "Meetings",
      value: kpis.meetings?.value?.toLocaleString() || "0",
      dotColor: "#FF4848",
    },
  ];

  return (
    <>
      {/* KPI Strip */}
      <KpiStrip items={stripKpis} />

      {/* Campaign Performance */}
      <div className={styles.g1}>
        <Card>
          <CardHead
            title="Campaign Performance"
            subtitle="All campaigns this period"
          />
          <div className={styles.overflowAuto}>
            <table className={styles.tbl}>
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Sent</th>
                  <th>Opened</th>
                  <th>Open Rate</th>
                  <th>Replied</th>
                  <th>Reply Rate</th>
                  <th>Meetings</th>
                </tr>
              </thead>
              <tbody>
                {campaignPerformance.map((c, i) => (
                  <tr key={c.campaign_id || i}>
                    <td className={styles.tdBold}>{c.name || c.campaign}</td>
                    <td>
                      <StatusChip
                        label={c.status || "Active"}
                        variant={
                          c.status?.toLowerCase() === "active"
                            ? "green"
                            : c.status?.toLowerCase() === "completed"
                              ? "blue"
                              : c.status?.toLowerCase() === "paused"
                                ? "orange"
                                : "green"
                        }
                      />
                    </td>
                    <td>{c.sent?.toLocaleString() || "0"}</td>
                    <td>{c.opened?.toLocaleString() ?? "0"}</td>
                    <td>
                      {c.open_rate != null
                        ? `${c.open_rate}%`
                        : (c.openRate ?? "0%")}
                    </td>
                    <td>{c.replied?.toLocaleString() ?? "0"}</td>
                    <td className={styles.tdGreen}>
                      {c.reply_rate != null
                        ? `${c.reply_rate}%`
                        : (c.replyRate ?? "0%")}
                    </td>
                    <td className={styles.tdAccent}>{c.meetings || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                disabled={page === 0}
                onClick={() => onPageChange(page - 1)}
              >
                Prev
              </button>
              <span className={styles.pageInfo}>
                Page {page + 1} of {totalPages}
                {pagination?.total ? ` · ${pagination.total} campaigns` : ""}
              </span>
              <button
                className={styles.pageBtn}
                disabled={page >= totalPages - 1}
                onClick={() => onPageChange(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </Card>
      </div>

      {/* Sentiment & Trends */}
      <div className={styles.g2}>
        <Card>
          <CardHead
            title="Reply Sentiment"
            subtitle="Quality of replies per campaign"
          />
          <DashboardChart
            config={getReplySentimentConfig(data?.reply_sentiment)}
            height={220}
          />
        </Card>
        <Card>
          <CardHead title="Reply Rate Trend" subtitle="Week over week" />
          <DashboardChart
            config={getReplyRateTrendConfig(data?.reply_rate_trend)}
            height={220}
          />
        </Card>
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════
const TABS = [
  { id: "overview", label: "Overview" },
  // { id: "activity", label: "SDR Activity" },
  { id: "campaigns", label: "Campaigns" },
];

const TAB_PANELS = {
  overview: OverviewTab,
  activity: ActivityTab,
  campaigns: CampaignsTab,
};

const NewDashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [campaignPage, setCampaignPage] = useState(0);
  const [meetingPage, setMeetingPage] = useState(1);
  const [repliesDialogOpen, setRepliesDialogOpen] = useState(false);

  const { activeTourKey } = useTour();
  const isDashboardTourActive = activeTourKey === "dashboard";

  const overviewData = useSelector((state) => state.newDashboardData);
  const insightsData = useSelector((state) => state.campaignInsights);
  const engagementData = useSelector((state) => state.engagementDetails);
  const campaignDashboardData = useSelector((state) => state.campaignDashboard);
  const meetingLogsData = useSelector((state) => state.meetingLogs);
  const paginationSelector = useSelector((state) => state.getPaginationQuery);

  const overview = isDashboardTourActive ? DASHBOARD_TOUR_DUMMY_OVERVIEW : overviewData?.payload;
  const insights = isDashboardTourActive ? DASHBOARD_TOUR_DUMMY_INSIGHTS : insightsData?.payload;
  const engagement = isDashboardTourActive ? DASHBOARD_TOUR_DUMMY_ENGAGEMENT : engagementData?.payload;
  const campaignDashboard = isDashboardTourActive
    ? { payload: DASHBOARD_TOUR_DUMMY_CAMPAIGN_DASHBOARD, loading: false }
    : campaignDashboardData;

  const isLoading =
    overviewData?.loading || insightsData?.loading || engagementData?.loading;
  const isCampaignLoading = isDashboardTourActive ? false : campaignDashboardData?.loading;

  const headerData = [{ id: "dashboard", label: STRINGS.DASHBOARD }];

  // Use a ref to store date range - avoids re-rendering DateRangePicker
  const dateRangeRef = React.useRef(getDefaultDashboardDateRange());

  const emptyInitDates = React.useMemo(() => ({}), []);

  const fetchMeetingLogs = useCallback(
    (page = 1, range = dateRangeRef.current) => {
      const payload = {
        page,
        limit: 10,
      };

      if (range?.to) {
        payload.date = formatDateForQuery(range.to);
      }

      dispatch(getMeetingLogs(navigate, payload));
    },
    [dispatch, navigate],
  );

  const fetchCampaignDashboard = useCallback(
    (range, page = 0) => {
      const appliedRange = range !== undefined ? range : dateRangeRef.current;
      const payload = {
        campaign_ids: [],
        page,
        limit: 10,
      };

      if (appliedRange && appliedRange.from && appliedRange.to) {
        payload.date_range = appliedRange;
      }

      dispatch(getCampaignDashboardData(navigate, payload));
    },
    [dispatch, navigate],
  );

  const fetchAllData = useCallback(
    (range) => {
      const appliedRange = range !== undefined ? range : dateRangeRef.current;
      const payload = {
        campaign_ids: [],
      };

      if (appliedRange && appliedRange.from && appliedRange.to) {
        payload.date_range = appliedRange;
      }

      console.log("[Dashboard] fetchAllData firing all APIs with:", {
        applied_range: appliedRange,
        date_range_in_payload: payload.date_range || "(omitted — all_time)",
        from_local: appliedRange?.from
          ? new Date(appliedRange.from).toString()
          : "n/a",
        to_local: appliedRange?.to
          ? new Date(appliedRange.to).toString()
          : "n/a",
      });

      // overview-stats (getNewDashboardData) merges manually-logged Daily
      // Report data that is stored against the user's LOCAL calendar date.
      // Backend asked FE to send start_date / end_date as local YYYY-MM-DD
      // alongside the existing from/to epochs so the manual-data merge
      // doesn't shift by the tz offset. This addition is scoped to
      // overview-stats only — the other three dashboard APIs stay as-is.
      const overviewPayload = payload.date_range
        ? {
            ...payload,
            date_range: {
              ...payload.date_range,
              start_date: toLocalDate(payload.date_range.from),
              end_date: toLocalDate(payload.date_range.to),
            },
          }
        : payload;

      dispatch(getNewDashboardData(navigate, overviewPayload));
      dispatch(getCampaignInsights(navigate, payload));
      dispatch(
        getEngagementDetails(navigate, { ...payload, page: 0, limit: 10 }),
      );
      setCampaignPage(0);
      fetchCampaignDashboard(appliedRange, 0);
      fetchMeetingLogs(1, appliedRange);
    },
    [dispatch, navigate, fetchCampaignDashboard, fetchMeetingLogs],
  );

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Refetch campaign dashboard when page changes
  useEffect(() => {
    if (activeTab === "campaigns") {
      fetchCampaignDashboard(dateRangeRef.current, campaignPage);
    }
  }, [campaignPage, activeTab, fetchCampaignDashboard]);

  // Refetch meeting logs when pagination changes
  useEffect(() => {
    const currentPage =
      paginationSelector?.data?.["meeting_log_table"]?.query?.page || 1;
    if (currentPage !== meetingPage) {
      setMeetingPage(currentPage);
      fetchMeetingLogs(currentPage, dateRangeRef.current);
    }
  }, [paginationSelector, meetingPage, fetchMeetingLogs]);

  const tabInfo = TAB_CONFIG[activeTab];
  const ActivePanel = TAB_PANELS[activeTab];

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      if (tabId === "campaigns") {
        fetchCampaignDashboard(dateRangeRef.current, campaignPage);
      }
    },
    [fetchCampaignDashboard, campaignPage],
  );

  const handleCampaignPageChange = useCallback((newPage) => {
    setCampaignPage(newPage);
  }, []);

  const handleDateSubmit = useCallback(
    (payload) => {
      const normalizedRange = normalizeDashboardDateRange(payload);
      dateRangeRef.current = normalizedRange;
      fetchAllData(normalizedRange);
    },
    [fetchAllData],
  );

  const handleDateReset = useCallback(() => {
    const defaultRange = getDefaultDashboardDateRange();
    dateRangeRef.current = defaultRange;
    fetchAllData(defaultRange);
  }, [fetchAllData]);

  return (
    <div className={styles.dashboardContainer} style={CSS_VARS}>
      <CustomHeader items={headerData} />

      {/* ── Dashboard Header ── */}
      <div className={styles.hdr}>
        <div>
          <h1 className={styles.hdrTitle}>{tabInfo.title}</h1>
          <p className={styles.hdrSubtitle}>{tabInfo.subtitle}</p>
        </div>
        <div className={styles.hdrRight}>
          {/* Date Range Picker */}
          <div className={styles.dateRangeWrapper}>
            <DashboardDateRangePicker
              onSubmit={handleDateSubmit}
              onReset={handleDateReset}
            />
          </div>
          {/* <button className={styles.hdrBtnActive}>This Month</button>
          <button className={styles.hdrBtn}>Last 90 Days</button> */}
          {/* <button className={styles.hdrBtn}>All SDRs</button> */}
          {/* <button className={styles.hdrExport}>Export Report</button> */}
        </div>
      </div>

      {/* ── Tabs - Overridden styling via CSS module ── */}
      <div className={styles.tabsContainer}>
        <div data-tour="dashboard-tabs" style={{ display: "flex" }}>
          {TABS.map((tab) => (
            <div
              className={styles.tabItem}
              key={tab.id}
              data-tour={
                tab.id === "overview"
                  ? "dashboard-tab-overview"
                  : tab.id === "campaigns"
                  ? "dashboard-tab-campaigns"
                  : undefined
              }
            >
              <FilterTab
                label={tab.label}
                isActive={activeTab === tab.id}
                onClick={() => handleTabChange(tab.id)}
              />
              <div
                className={
                  activeTab === tab.id
                    ? styles.tabIndicatorActive
                    : styles.tabIndicator
                }
              />
            </div>
          ))}
        </div>
      </div>
      <div className={styles.tabLine} />

      {/* ── Tab Content (key forces re-mount animation per tab) ── */}
      <div
        className={styles.cnt}
        style={{ position: "relative", minHeight: "100vh" }}
      >
        {activeTab === "campaigns" ? (
          isCampaignLoading ? (
            <DashboardLoader message="Fetching campaign analytics..." />
          ) : (
            <div className={styles.viewActive} key={activeTab}>
              <CampaignsTab
                campaignDashboard={campaignDashboard}
                page={campaignPage}
                onPageChange={handleCampaignPageChange}
              />
            </div>
          )
        ) : isLoading ? (
          <DashboardLoader message="Fetching your latest analytics..." />
        ) : (
          <div className={styles.viewActive} key={activeTab}>
            <ActivePanel
              overview={overview}
              insights={insights}
              engagement={engagement}
              meetingLogsData={meetingLogsData}
              meetingPage={meetingPage}
              onTotalRepliesClick={() => setRepliesDialogOpen(true)}
            />
          </div>
        )}
      </div>

      {/* Total Replies drill-down */}
      <RepliesListDialog
        open={repliesDialogOpen}
        onClose={() => setRepliesDialogOpen(false)}
        dateRange={dateRangeRef.current}
        navigate={navigate}
      />
    </div>
  );
};

export default NewDashboardScreen;
